/**
 * Pure helpers for the Upload App screen (kept out of the component so they can
 * be unit-tested without rendering). The API counterpart is api/src/uploads.js;
 * the accepted types and field names must agree with it.
 */

export const UPLOAD_TYPES = {
  apk: { binType: "apk", platform: "android", label: "Android APK" },
  crx: { binType: "crx", platform: "chrome", label: "Chrome / Edge extension (CRX)" },
};

/** Client-side size limit in MB; must not exceed the API's MAX_UPLOAD_MB (default 500). */
export const MAX_UPLOAD_MB = Number(process.env.REACT_APP_MAX_UPLOAD_MB) > 0 ? Number(process.env.REACT_APP_MAX_UPLOAD_MB) : 500;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** Work out the binary type from a file name; null when it is not an .apk or .crx. */
export function classifyUploadFile(name) {
  const match = /\.([a-z0-9]+)$/i.exec(String(name || ""));
  const ext = match ? match[1].toLowerCase() : "";
  return UPLOAD_TYPES[ext] ? { ...UPLOAD_TYPES[ext], ext } : null;
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Validation for the fields the API requires; returns { field: message } for each problem. */
export function validateUploadForm({ appName, file, maxBytes = MAX_UPLOAD_BYTES }) {
  const errors = {};
  if (!String(appName || "").trim()) errors.appName = "Enter a name for the app.";
  if (!file) {
    errors.file = "Choose the .apk or .crx file to upload.";
  } else if (!classifyUploadFile(file.name)) {
    errors.file = `"${file.name}" is not a supported type. Upload an Android .apk or a Chrome/Edge .crx file.`;
  } else if (file.size > maxBytes) {
    errors.file = `"${file.name}" is ${formatBytes(file.size)}; the limit is ${Math.round(maxBytes / 1024 / 1024)} MB.`;
  } else if (file.size === 0) {
    errors.file = `"${file.name}" is empty.`;
  }
  return errors;
}

/** Build the multipart body the API expects (field names match api/src/uploads.js). */
export function buildUploadFormData(fields, file) {
  const formData = new FormData();
  formData.append("file", file); // the File carries its own name; the API only looks at the extension
  formData.append("appName", String(fields.appName || "").trim());
  formData.append("appStoreUrl", fields.appStoreUrl || "");
  formData.append("description", fields.appDescription || "");
  formData.append("appDeveloper", fields.appDeveloper || "");
  formData.append("appIconUrl", fields.appIconUrl || "");
  formData.append("appRating", fields.appRating || "");
  formData.append("appDownloads", fields.appDownloads || "");
  return formData;
}

/** Progress percentage from an axios upload progress event (null when the total is unknown). */
export function uploadPercent(event) {
  if (!event || !event.total) return null;
  return Math.min(100, Math.round((event.loaded / event.total) * 100));
}

/** A message the user can act on for a failed upload request. */
export function describeUploadError(err, { maxMb = MAX_UPLOAD_MB } = {}) {
  if (!err) return "The upload failed.";
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.message === "canceled") {
    return "Upload cancelled.";
  }
  const response = err.response;
  if (!response) {
    return "The connection was interrupted before the upload finished. Check your connection and try again.";
  }
  const serverMessage = response.data && response.data.error && response.data.error.message;
  switch (response.status) {
    case 413:
      return serverMessage || `The file is larger than the server allows (${maxMb} MB).`;
    case 401:
    case 403:
      return serverMessage || "You are not allowed to upload apps. Ask an organisation admin to enable this for your account.";
    case 400:
      return serverMessage || "The server rejected the upload. Check the file and try again.";
    default:
      return serverMessage || `The upload failed (HTTP ${response.status}). Try again in a moment.`;
  }
}
